import appConfig from '@nerdwallet/app-config';
import classes from '@nerdwallet/base-styles/classes';
import { CenteredContainer, Text } from '@nerdwallet/currency';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import WpEmbed from '@nerdwallet/react-wp-embed';
import { isInBuilder } from '~/app/lib/helpers';

import styles from './third-party-embed.module.scss';

const isValidUrl = (url: string) => !!url.match(/^(http|https):\/\//);

const ResizableIframe = ({
  src,
  height,
  width,
  style,
}: {
  src: string;
  height?: string;
  width?: string;
  style: React.CSSProperties;
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState<string | number | undefined>(
    height
  );

  useEffect(() => {
    const iframe = iframeRef.current;
    let observer: ResizeObserver | null = null;

    const onLoad = () => {
      try {
        const doc = iframe?.contentDocument ?? iframe?.contentWindow?.document;
        if (!doc?.body) return;
        observer = new ResizeObserver(([entry]) => {
          setIframeHeight(entry.contentRect.height);
        });
        observer.observe(doc.body);
      } catch {
        // Cross-origin iframe: content document is inaccessible, use provided height
      }
    };

    iframe?.addEventListener('load', onLoad);
    return () => {
      iframe?.removeEventListener('load', onLoad);
      observer?.disconnect();
    };
  }, []);

  return (
    <iframe
      ref={iframeRef}
      src={src}
      width={width}
      height={iframeHeight}
      scrolling="no"
      style={style}
      title="Embedded content"
    />
  );
};

const scriptEmbedContent = (scriptSrc: string) => {
  const htmlSnippet = `
    <script
      src="${scriptSrc}">
    </script>
  `;
  if (!scriptSrc) {
    return null;
  }
  return (
    <span
      id="script-embed-container"
      style={{ textAlign: 'left' }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: htmlSnippet }}
    />
  );
};

const iframeEmbedContent = (
  iframeSrc: string,
  height: string,
  width: string
) => {
  return (
    isValidUrl(iframeSrc) && (
      <div id="iframe-wrapper" className={styles.iframeBox}>
        <ResizableIframe
          src={iframeSrc}
          height={height || undefined}
          width={width || undefined}
          style={{
            width: '100%',
            minWidth: width || '100%',
            height: height || '100%',
          }}
        />
      </div>
    )
  );
};

const calculatorEmbedContent = (cffId: string, pathname: string) => {
  const iframeUrl = `${appConfig.SITE_BASE_URL}/blog/cff/?form_id=${cffId}`;

  const defaultEditorOutput = () => {
    if (isInBuilder(pathname)) {
      return (
        <CenteredContainer
          className={classNames([
            'calculator__default-output',
            'landing-page-standard__max-widths',
          ])}
        >
          <div
            className={classNames([
              classes('padding-vertical-5'),
              classes('text-align-center'),
            ])}
          >
            <Text size="large" bold align="center">
              Enter a CFF ID to make a calculator appear
            </Text>
          </div>
        </CenteredContainer>
      );
    }
    return null;
  };

  if (!cffId) return defaultEditorOutput();

  return (
    <div className={styles.calculator}>
      <WpEmbed src={iframeUrl} />
    </div>
  );
};

const BaseThirdPartyEmbed = ({
  embedType,
  iframeSrc,
  scriptSrc,
  cffId,
  height,
  width,
  pathname,
}: {
  embedType: string;
  iframeSrc: string;
  scriptSrc: string;
  cffId: string;
  height: string;
  width: string;
  pathname: string;
}) => {
  const type = {
    iframe: iframeEmbedContent(iframeSrc, height, width),
    script: scriptEmbedContent(scriptSrc),
    calculator: calculatorEmbedContent(cffId, pathname),
  };

  if (
    (embedType === 'script' && !scriptSrc) ||
    (embedType === 'iframe' && !iframeSrc)
  ) {
    return (
      <div id="default-empty-src-message" style={{ margin: '2rem 0' }}>
        <Text size="large" bold>
          Enter a {embedType} URL to make the content appear
        </Text>
      </div>
    );
  }

  return type[embedType as keyof typeof type];
};

export default BaseThirdPartyEmbed;
